import { useEffect, useMemo, useState } from "react";
import { useCollection } from "@/hooks/useCollection";
import { useSiteSettings } from "@/hooks/content";
import { supabase } from "@/lib/supabase";
import { scheduleToday, type ScheduleItem } from "@/mocks/schedule";
import { PageHeader, useToast } from "./components/admin-ui";

/** Parse "HH:MM" into minutes-since-midnight. */
function toMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function formatTime(time: string): string {
  const mins = toMinutes(time);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
}

/**
 * Program Reminders — surfaces programs starting soon (within the next ~10
 * minutes) and missing/gap slots, then lets the admin send an email alert
 * and/or a WhatsApp reminder to the configured number.
 */
export default function ProgramRemindersPage() {
  const { show } = useToast();
  const { items: schedule } = useCollection<ScheduleItem>("ltv_schedule_v1", scheduleToday);
  const { record: settings } = useSiteSettings();

  const [nowMinutes, setNowMinutes] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Refresh the clock every 30 seconds so "starting soon" stays accurate.
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setNowMinutes(d.getHours() * 60 + d.getMinutes());
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const sorted = useMemo(
    () => [...schedule].sort((a, b) => toMinutes(a.time) - toMinutes(b.time)),
    [schedule],
  );

  // Programs starting within the next 10 minutes.
  const startingSoon = useMemo(() => {
    return sorted.filter((p) => {
      const start = toMinutes(p.time);
      const diff = start - nowMinutes;
      return diff >= -1 && diff <= 10;
    });
  }, [sorted, nowMinutes]);

  // Gaps of more than 45 minutes between consecutive programs (potential missing slot).
  const gaps = useMemo(() => {
    const out: { after: string; before: string; gapMinutes: number }[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      const gap = toMinutes(b.time) - toMinutes(a.time);
      if (gap > 45) {
        out.push({ after: a.time, before: b.time, gapMinutes: gap });
      }
    }
    return out;
  }, [sorted]);

  const waNumber = (settings.whatsapp ?? "").replace(/\D/g, "");
  const reminderText = startingSoon
    .map((p) => `• ${p.time} — ${p.program}`)
    .join("\n");

  async function sendEmailReminder() {
    if (!settings.email) {
      show("Set an admin email in Website Settings first.", "error");
      return;
    }
    if (startingSoon.length === 0) {
      show("No programs are starting within the next 10 minutes.", "error");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          to: settings.email,
          subject: `L+TV program reminder — ${startingSoon.length} starting soon`,
          html: `<div style="font-family:Arial,sans-serif;max-width:520px;margin:0 auto;padding:24px;color:#1f2937;"><h1 style="font-size:18px;margin:0 0 12px;">Program Reminder</h1><p style="font-size:14px;color:#6b7280;">The following programs are starting soon:</p><ul style="font-size:14px;line-height:1.8;">${startingSoon
            .map((p) => `<li><strong>${formatTime(p.time)}</strong> — ${p.program}</li>`)
            .join("")}</ul></div>`,
        },
      });
      if (error) throw error;
      if (data?.ok) {
        setResult({ ok: true, message: data.message || "Reminder email sent." });
        show("Reminder email sent.");
      } else {
        setResult({ ok: false, message: data?.error || "Failed to send reminder." });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to send reminder.";
      setResult({ ok: false, message: msg });
      show(msg, "error");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Program Reminders"
        subtitle="Monitor upcoming programs, missing slots, and alert the team by email or WhatsApp."
      />

      {result && (
        <div
          className={`text-sm rounded-md p-3 flex items-start gap-2 ${
            result.ok
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-accent-50 border border-accent-200 text-accent-700"
          }`}
        >
          <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
            <i className={`${result.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-sm`} />
          </span>
          <span>{result.message}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Starting soon */}
        <div className="bg-white rounded-lg border border-background-200 p-5">
          <h2 className="text-sm font-semibold text-foreground-900 mb-1">Starting soon (next 10 min)</h2>
          <p className="text-xs text-foreground-500 mb-4">
            Current time: {formatTime(`${String(Math.floor(nowMinutes / 60)).padStart(2, "0")}:${String(nowMinutes % 60).padStart(2, "0")}`)}
          </p>

          {startingSoon.length === 0 ? (
            <div className="flex items-start gap-2 text-sm text-foreground-600 bg-background-50 rounded-md p-4">
              <i className="ri-time-line text-foreground-400 mt-0.5" />
              No programs are starting in the next 10 minutes.
            </div>
          ) : (
            <ul className="space-y-2 mb-4">
              {startingSoon.map((p) => (
                <li key={p.id} className="flex items-center gap-3 bg-accent-50 border border-accent-200 rounded-md px-3 py-2.5">
                  <span className="text-sm font-bold text-accent-700 whitespace-nowrap">{formatTime(p.time)}</span>
                  <span className="text-sm text-foreground-800">{p.program}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={sendEmailReminder}
              disabled={sending || startingSoon.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-send-line text-sm" />
              </span>
              {sending ? "Sending…" : "Send email reminder"}
            </button>
            {waNumber && startingSoon.length > 0 && (
              <a
                href={`https://wa.me/${waNumber}?text=${encodeURIComponent("L+TV program reminder:\n" + reminderText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-[#25D366] text-white text-sm font-medium rounded-md hover:bg-[#1ebe5d] whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-sm" />
                </span>
                Send on WhatsApp
              </a>
            )}
          </div>
        </div>

        {/* Gaps / missing slots */}
        <div className="bg-white rounded-lg border border-background-200 p-5">
          <h2 className="text-sm font-semibold text-foreground-900 mb-1">Possible missing slots</h2>
          <p className="text-xs text-foreground-500 mb-4">
            Gaps longer than 45 minutes in today's schedule.
          </p>

          {gaps.length === 0 ? (
            <div className="flex items-start gap-2 text-sm text-foreground-600 bg-background-50 rounded-md p-4">
              <i className="ri-checkbox-circle-line text-green-500 mt-0.5" />
              No large gaps detected — schedule looks complete.
            </div>
          ) : (
            <ul className="space-y-2">
              {gaps.map((g, i) => (
                <li key={i} className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-md px-3 py-2.5">
                  <i className="ri-alert-line text-accent-500" />
                  <span className="text-sm text-foreground-800">
                    {g.after} → {g.before}
                  </span>
                  <span className="text-xs text-foreground-500 ml-auto whitespace-nowrap">
                    {g.gapMinutes} min gap
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Today's full schedule */}
      <div className="bg-white rounded-lg border border-background-200 p-5">
        <h2 className="text-sm font-semibold text-foreground-900 mb-4">Today's schedule</h2>
        <div className="divide-y divide-background-50 max-h-80 overflow-y-auto">
          {sorted.map((p) => {
            const start = toMinutes(p.time);
            const isNow = start <= nowMinutes && start + 30 >= nowMinutes;
            return (
              <div key={p.id} className="flex items-center gap-3 py-2">
                <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap w-20">
                  {p.time}
                </span>
                <span className="text-sm text-foreground-800 flex-1 truncate">{p.program}</span>
                {isNow && (
                  <span className="text-[10px] font-bold bg-accent-500 text-white px-1.5 py-0.5 rounded whitespace-nowrap">
                    ON AIR
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}